import { MonatsabschlussZeile } from "@/api/Abrechnung";
import { MONATSNAMEN } from "@/api/Termine";
import { Moment } from "moment";
import { Content, ContentTable, ContentText, Table } from "pdfmake/interfaces";
import { format } from '../util';

export interface Tag {
  miete: number;
  steuer: number;
  abrechnungDame?: string;
  abrechnungStudio?: string;
}

export function createMonatsblattTabelle(
  monat: Moment,
  zeile: MonatsabschlussZeile,
  anzahlTage: number,
  tage: Tag[]
): ContentTable {
  const content: Content[][] = [];
  const wochensummen: { miete: number; steuer: number }[] = [];
  const summen = { miete: 0, steuer: 0 };

  for (let tag = 1; tag <= anzahlTage; tag++) {
    tage.push({ miete: 0, steuer: 0 });
    content.push([
      {
        text: (tag < 10 ? "0" : "") + tag,
        style: "tableHeader",
        alignment: "center",
      },
      { text: "", alignment: "center" },
      { text: "", alignment: "center" },
      { text: "", alignment: "center" },
      { text: "", alignment: "center" },
    ]);
    if (tag % 7 == 0) {
      content.push([
        { text: "Summe", style: "tableSummeHeader", alignment: "center" },
        { text: "0 €", style: "tableSummeHeader", alignment: "center" },
        { text: "0 €", style: "tableSummeHeader", alignment: "center" },
        { text: "", style: "tableSummeHeader", colSpan: 2 } as Content,
        { text: "", style: "tableSummeHeader" },
      ]);
      wochensummen.push({ miete: 0, steuer: 0 });
    }
  }
  if (anzahlTage != 28) {
    content.push([
      { text: "Summe", style: "tableSummeHeader", alignment: "center" },
      { text: "0 €", style: "tableSummeHeader", alignment: "center" },
      { text: "0 €", style: "tableSummeHeader", alignment: "center" },
      {
        text: "",
        style: "tableSummeHeader",
        alignment: "center",
        colSpan: 2,
      } as Content,
      { text: "", style: "tableSummeHeader" },
    ]);
    wochensummen.push({ miete: 0, steuer: 0 });
  }

  // Letzte Tabellenzeile mit Endsummen
  content.push([
    { text: "Endbetrag", style: "tableSummeHeader", alignment: "center" },
    { text: "0 €", style: "tableSummeHeader", alignment: "center" },
    { text: "0 €", style: "tableSummeHeader", alignment: "center" },
    {
      text: "",
      style: "tableSummeHeader",
      alignment: "center",
      colSpan: 2,
    } as Content,
    { text: "", style: "tableSummeHeader" },
  ]);

  zeile.tage?.forEach((tag) => {
    const numTag = parseInt(tag.buchungstag);
    const wochenIndex = Math.ceil(numTag / 7) - 1;
    const zeilenIndex = numTag + wochenIndex - 1;
    const tagesSumme = tage[numTag - 1];
    const tabellenzeile = content[zeilenIndex];

    if (tag.tag == tag.buchungstag) {
      tagesSumme.abrechnungDame = tag.unterschriftDame;
      tagesSumme.abrechnungStudio = tag.unterschriftStudio;
    }

    tagesSumme.miete += tag.miete;
    (tabellenzeile[1] as ContentText).text = format(tagesSumme.miete);
    wochensummen[wochenIndex].miete += tag.miete;
    summen.miete += tag.miete;

    if (tag.steuer > 0) {
      tagesSumme.steuer += tag.steuer;
      (tabellenzeile[2] as ContentText).text = format(tagesSumme.steuer);
      wochensummen[wochenIndex].steuer += tag.steuer;
      summen.steuer += tag.steuer;
    }

    if (tag.miete > 0) {
      // Unterschriften rendern
      //tagesSumme.abrechnungDame = "unterschriftDame";
      if (tagesSumme.abrechnungDame) {
        const unterschriftDame = tabellenzeile[3] as any;
        delete unterschriftDame.text; // Statt Text wollen wir ein Bild
        unterschriftDame.image = tagesSumme.abrechnungDame;
        unterschriftDame.fit = [138, 24];
        unterschriftDame.relativePosition = { x: 0, y: -5 };
      }
      tagesSumme.abrechnungStudio = "unterschriftStudio";
      if (tagesSumme.abrechnungStudio) {
        // (tabellenzeile[4] as ContentImage).image
        const unterschriftStudio = tabellenzeile[4] as any;
        delete unterschriftStudio.text; // Statt Text wollen wir ein Bild
        unterschriftStudio.image = tagesSumme.abrechnungStudio;
        unterschriftStudio.fit = [138, 24];
        unterschriftStudio.relativePosition = { x: 0, y: -5 };
      }
    }
  });
  wochensummen.forEach((summe, index) => {
    const zeilenIndex =
      index == wochensummen.length - 1
        ? content.length - 2
        : (index + 1) * 8 - 1;
    (content[zeilenIndex][1] as ContentText).text = format(summe.miete);
    (content[zeilenIndex][2] as ContentText).text = format(summe.steuer);
  });
  (content[content.length - 1][1] as ContentText).text = format(summen.miete);
  (content[content.length - 1][2] as ContentText).text = format(summen.steuer);

  const table: Table = {
    widths: [70, 73, 83, 164, 151],
    headerRows: 4,
    keepWithHeaderRows: 1,
    body: [
      [
        {
          text: "ZNA GmbH",
          colSpan: 2,
          rowSpan: 3,
          alignment: "center",
          style: "tableLogo",
        },
        {},
        { text: "Name", style: "tableHeader", alignment: "left" },
        { text: zeile.dame?.name, colSpan: 2, alignment: "center" },
        {},
      ],
      [
        {},
        {},
        { text: "Künstlername", style: "tableHeader", alignment: "left" },
        { text: zeile.dame?.kuenstlername, alignment: "center" },
        { text: "Im Monat", style: "tableHeader", alignment: "center" },
      ],
      [
        {},
        {},
        { text: "Steuernummer", style: "tableHeader", alignment: "left" },
        { text: zeile.dame?.steuernummer || "--", alignment: "center" },
        {
          text: MONATSNAMEN[monat.month()] + " / " + monat.year(),
          style: "tableHeader",
          alignment: "center",
        },
      ],
      [
        {
          text: "An folgenden Tagen",
          style: "tableHeader",
          alignment: "center",
        },
        {
          text: "Betrag lt. Vereinbarung in €",
          style: "tableHeader",
          alignment: "center",
        },
        {
          text: "Düsseldorfer Verfahren Tag 25 €",
          style: "tableHeader",
          alignment: "center",
        },
        {
          text: "Unterschrift Dame",
          style: "tableHeader",
          alignment: "center",
        },
        {
          text: "Unterschrift Studio",
          style: "tableHeader",
          alignment: "center",
        },
      ],
      ...content,
    ],
  };

  return {
    style: "tableExample",
    color: "#444",
    table,
  };
}
